/**
 * FEATURE CATALOG — the typed configuration framework layered on top of the
 * persona/module identity already defined in `roles.ts` (PR #96).
 *
 * `roles.ts` owns the persona codes and the PERSONA_MODULES seed (persona →
 * module-id[]). This file does NOT re-declare that seed. It adds the missing
 * typed layer: a `ModuleId` union, runtime guards, and a `FEATURE_CATALOG` that
 * maps each module id to the SURFACE it gates (a MemberProfile tab, an app
 * route, or a not-yet-built feature). The access layer (`src/access/*`) reads
 * from here. Keep this the single source for module → surface mapping.
 *
 * SINGLE SOURCE OF TRUTH: the set of module ids is anchored to `roles.ts`.
 * `roles.ts` types `PERSONA_MODULES` values as `ModuleId[]` (imported from here),
 * so a module id in the persona seed that is not a member of the `ModuleId` union
 * is a COMPILE-TIME error in `roles.ts`. In addition, the DEV-only runtime check
 * at the bottom of this file logs a `console.warn` if a seed id lacks a
 * FEATURE_CATALOG entry — a redundant belt-and-braces guard, no-op in production.
 */

// `import type` only — @astrana/ui/modules ships a runtime enum (ModuleType),
// but this file only needs the TYPE. A value import would pull the real
// runtime module into this catalog (which `roles.ts` imports transitively for
// its ModuleId type), and @astrana/ui does not resolve cleanly under Vitest's
// module graph in every context. String literals below are cast `as
// ModuleType` — they must stay byte-identical to the real enum's string
// values (see the comment above CARD_MODULE_TYPES).
// eslint-disable-next-line import/no-unresolved
import type { ModuleType } from "@astrana/ui/modules";

import { PERSONA_MODULES } from "./roles";

// ---------------------------------------------------------------------------
// ModuleId — the gating module ids + the 9 Home card rendering ids.
// ---------------------------------------------------------------------------
/**
 * The module ids that personas can be granted. These are the same strings used
 * as values in `roles.ts` PERSONA_MODULES (the compile-time check below proves
 * the catalog covers every id that seed references).
 *
 * The trailing `Home*` ids are a SEPARATE vocabulary: they gate which of the 6
 * Home-tab CARDS render (ACT-1303), not which TAB is visible (that's still the
 * plain `"Home"` id above, granted/denied as a whole by PERSONA_MODULES). They
 * are deliberately NOT added to PERSONA_MODULES — see isHomeCardModuleId.
 */
export type ModuleId =
  | "Home"
  | "Medications"
  | "Diagnoses"
  | "Labs"
  | "Scribe"
  | "AmbientAI"
  | "Translator"
  | "WriteBack"
  | "Auths"
  | "MemberSearch"
  | "PriorityWorklist"
  | "Eligibility"
  | "Claims"
  | "CareTeam"
  | "Outreach"
  | "NetworkSearch"
  | "Documents"
  | "Notes"
  | "Labs"
  | "Diagnoses"
  | "PracticeSchedule"
  | "AdtNotifications"
  | "Activity"
  | "HomeNextBestAction"
  | "HomeClinicalSummary"
  | "HomeInpatientEd"
  | "HomeAnnualWellness"
  | "HomeQualityGaps"
  | "HomeHcc"
  | "HomeCases"
  | "HomeDemographics"
  | "HomeCareTeam";

// The Documents tab name. MemberProfilePage renders a real Documents tab (it
// mounts <MemberDocumentsPage/> as the tab body) and imports THIS constant for
// its `tabName`, so the catalog and the page cannot drift apart. Matching
// tabNames are what routes the tab through the persona gate + the tab-order sort.
export const DOCUMENTS_TAB = "Documents";

// ---------------------------------------------------------------------------
// FEATURE_CATALOG — module id → the surface it gates.
// ---------------------------------------------------------------------------
/**
 * What surface each module id controls:
 *   - `tab`     → a MemberProfile tab, matched by `tabName`.
 *   - `route`   → a full-page app route (e.g. Member Search at /members).
 *   - `feature` → a feature with no current FE surface, registered for the
 *                 future so the access layer recognises the id without crashing.
 */
export interface FeatureCatalogEntry {
  kind: "tab" | "route" | "feature" | "card";
  label: string;
  /** Set for `kind: 'tab'` — the MemberProfile ModuleGroup.tabName it controls. */
  tabName?: string;
  /** Set for `kind: 'route'` — the app route it controls. */
  route?: string;
  /**
   * The set of action keys this module supports — the REGISTRY of what CAN be
   * mapped for a persona. The backend's per-persona `actions[]` in
   * `connectCurrentUser.currentPersona.modules[].actions` decides which are
   * ENABLED at runtime; this list is the vocabulary the backend may reference.
   * Keep it minimal: only add keys the backend actually emits or plans to emit.
   */
  actions?: readonly string[];
  /**
   * Set for `kind: 'card'` — the @astrana/ui `ModuleType` this catalog id
   * renders as within the Home tab's card list. See CARD_MODULE_TYPES.
   */
  moduleType?: ModuleType;
}

// ---------------------------------------------------------------------------
// CARD_MODULE_TYPES — Home card ModuleId → @astrana/ui ModuleType (ACT-1303).
// ---------------------------------------------------------------------------
/**
 * Maps each `Home*` catalog id to the @astrana/ui `ModuleType` it renders.
 *
 * The right-hand values are cast `as ModuleType` from plain string literals
 * rather than referenced off the real `ModuleType` enum object. `ModuleType`
 * is imported `import type` only (see above) so this file never pulls in the
 * @astrana/ui runtime module — that runtime import is what breaks resolution
 * under Vitest in some contexts. The literals below MUST stay byte-identical
 * to `@astrana/ui/modules`'s `ModuleType` enum values (confirmed against
 * `node_modules/@astrana/ui/dist/components/modules/MemberProfile/MemberProfile.types.d.ts`):
 *   clinicalSummary, inpatientEd, annualWellness, qualityGaps, hcc, cases,
 *   demographics.
 * If @astrana/ui ever renames one of these enum values, this map silently
 * stops matching — there is no compile-time link between the string literal
 * and the real enum member (only the `ModuleType` type annotation, which
 * accepts any of its string values).
 */
export const CARD_MODULE_TYPES: Record<
  | "HomeNextBestAction"
  | "HomeClinicalSummary"
  | "HomeInpatientEd"
  | "HomeAnnualWellness"
  | "HomeQualityGaps"
  | "HomeHcc"
  | "HomeCases"
  | "HomeDemographics"
  | "HomeCareTeam",
  ModuleType
> = {
  HomeNextBestAction: "nextBestAction" as ModuleType,
  HomeClinicalSummary: "clinicalSummary" as ModuleType,
  HomeInpatientEd: "inpatientEd" as ModuleType,
  HomeAnnualWellness: "annualWellness" as ModuleType,
  HomeQualityGaps: "qualityGaps" as ModuleType,
  HomeHcc: "hcc" as ModuleType,
  HomeCases: "cases" as ModuleType,
  HomeDemographics: "demographics" as ModuleType,
  HomeCareTeam: "careTeam" as ModuleType,
};

/**
 * The default ordered list of the 9 Home card ModuleTypes — the fallback
 * order used when no persona-driven sort applies (e.g. an unconfigured
 * persona). Mirrors ALL_MODULES's Home group order in MemberProfilePage.tsx.
 * Next Best Action is FIRST — it is the only card that asks the user to DO
 * something, and the engine surfaces exactly one at a time, so it reads as a
 * banner above the identity summary. Demographics follows.
 */
export const HOME_CARD_TYPES: readonly ModuleType[] = [
  CARD_MODULE_TYPES.HomeNextBestAction,
  CARD_MODULE_TYPES.HomeDemographics,
  CARD_MODULE_TYPES.HomeCareTeam,
  CARD_MODULE_TYPES.HomeClinicalSummary,
  CARD_MODULE_TYPES.HomeInpatientEd,
  CARD_MODULE_TYPES.HomeAnnualWellness,
  CARD_MODULE_TYPES.HomeQualityGaps,
  CARD_MODULE_TYPES.HomeHcc,
  CARD_MODULE_TYPES.HomeCases,
];

export const FEATURE_CATALOG: Record<ModuleId, FeatureCatalogEntry> = {
  Home: { kind: "tab", label: "Home", tabName: "Home" },
  Eligibility: { kind: "tab", label: "Eligibility", tabName: "Eligibility" },
  Claims: {
    kind: "tab",
    label: "Claims",
    tabName: "Claims",
    // "printEob" gates the per-claim "Print EOB" CTA in the claim detail view.
    // Grant it to a persona (e.g. CUSTOMER_SERVICE) server-side to expose the
    // button; the admin ROLE also gets it via resolveModuleActions' all-access.
    actions: ["view", "submit", "printEob"],
  },
  // RETIRED AS A TAB — Care Team now renders as a Home CARD ("HomeCareTeam"
  // below). No ModuleGroup carries the "Care Team" tabName any more, so this
  // entry gates nothing at runtime. Left in place on purpose: live
  // `member_360.persona_modules` rows and the PERSONA_MODULES seed still
  // reference the "CareTeam" id, and the admin console still lists it. Removing
  // it requires a coordinated astraconnect-admin-console change + SQL.
  CareTeam: { kind: "tab", label: "Care Team", tabName: "Care Team" },
  Auths: {
    kind: "tab",
    label: "Auths",
    tabName: "Auths",
    actions: ["view", "create"],
  },
  Medications: {
    kind: "tab",
    label: "Medications",
    tabName: "Medications",
    actions: ["view", "edit"],
  },
  // AC-ADM-3 (2026-08-21) — Diagnoses / Labs / Scribe were the last MemberProfile
  // tabs with NO catalog entry, which meant `moduleIdForTabName` returned
  // undefined for them and BOTH the visibility gate (useVisibleModules' `if
  // (!moduleId) return true`) and the admin tab-order sort (useTabOrder's
  // sortOrder lookup) silently skipped them: always visible to every persona,
  // and permanently stranded at the tail of the tab strip. Registering them here
  // is the whole fix — the gate and the sort share this one reverse index.
  //
  // These three ids are seeded into `member_360.persona_modules.module_id` by
  // migration 0027 and MUST stay byte-identical to it. DEPLOY ORDER MATTERS: the
  // gate fails CLOSED for a persona with no row, so 0027 has to be applied to an
  // environment BEFORE this code reaches it or Diagnoses/Labs vanish there.
  Diagnoses: { kind: "tab", label: "Diagnoses", tabName: "Diagnoses" },
  Labs: { kind: "tab", label: "Labs", tabName: "Labs" },
  // Scribe (the ambient-recording panel) keeps its BUILD-TIME feature flag —
  // `featureFlags.ambientScribe || featureFlags.aiVisitNotes` decides whether
  // MemberProfilePage constructs the group at all — and now additionally requires
  // the persona grant, via this entry. Flag AND persona: neither alone shows it.
  Scribe: { kind: "tab", label: "Scribe", tabName: "Scribe" },
  // `submitPaf` (the digital PAF assessment write, ACT-1367) MUST be registered
  // here, not just granted per-persona. `resolveModuleActions` case (0) returns
  // `catalogActions(moduleId)` for the ADMIN role BEFORE it ever looks at the
  // persona, so an unregistered action is unreachable for an admin no matter
  // what `member_360.persona_modules.actions` says — which is why the Submit PAF
  // button was invisible to every persona including admin.
  //
  // Consistent with the other write actions (Claims `submit`, Auths `create`):
  // registering grants it to the admin bypass and to the enforcement-off
  // transition seam (case 1), while a persona can still narrow it via its own
  // explicit `actions` row (case 3b). Contrast HomeQualityGaps below, whose
  // omission IS deliberate and documented.
  Documents: {
    kind: "tab",
    label: "Documents",
    tabName: DOCUMENTS_TAB,
    actions: ["submitPaf"],
  },
  MemberSearch: {
    kind: "route",
    label: "Member Search",
    route: "/members",
    actions: ["search"],
  },
  // G1 (2026-08-19/20) — the chart's read-out of visit notes + the entry point
  // for drafting a new one (see MemberProfilePage.tsx's NotesTab mount). Was
  // previously gated ONLY by the ambientScribe/aiVisitNotes feature flag, so
  // every persona — including CUSTOMER_SERVICE — could see clinical note
  // content. Gated the same way as every other tab here: persona membership
  // via canSeeModule/useModuleEnabled, ANDed onto the existing feature flag.
  Notes: { kind: "tab", label: "Notes", tabName: "Notes" },
  // P6 host wiring (patient-priority-index) — native @astrana/ui module, a
  // provider's ranked patient worklist. Mirrors MemberSearch's `route` kind
  // (a full top-level page, not a MemberProfile tab). Gated by BOTH
  // featureFlags.patientPriorityIndex (VITE_FEATURE_PATIENT_PRIORITY_INDEX,
  // default off — module unpublished) AND persona (below). SHIP-GATE: the
  // backend's real persona `modules[]` grant must add this id before
  // enforcement is meaningful in an env with personaEnforcement on — until
  // then the no-persona-payload allow-all transition seam covers it, same as
  // every other module here.
  PriorityWorklist: {
    kind: "route",
    // Key stays `PriorityWorklist` — it is the persona-grant module id.
    label: "Patient Panel",
    route: "/worklist",
  },
  // Registered for the future — no current FE surface.
  AmbientAI: { kind: "feature", label: "Ambient AI" },
  // AC-TRX-1 — the live translator rail. A `feature`, not a `tab`: the rail is a
  // portaled overlay with its own trigger strip, not a MemberProfile tab, so
  // there is no tabName for the reverse lookup to match. Which personas get it
  // is DB/admin-console configuration (member_360 module row + persona binding,
  // surfaced through currentPersona.modules[]) — deliberately not a hardcoded
  // persona list here.
  Translator: { kind: "feature", label: "Translator" },
  WriteBack: { kind: "feature", label: "Write Back" },
  Outreach: {
    kind: "tab",
    label: "Outreach",
    tabName: "Outreach",
    // ACT-1253: bare "create" action (matches Auths) gates the create/log +
    // dispatch affordance. Backend grants it per-persona via
    // currentPersona.modules[].actions (ACT-1319 server-driven model).
    actions: ["view", "create"],
  },
  NetworkSearch: { kind: "feature", label: "Network Search" },
  // ACT-1239 — the provider's daily practice schedule, a full top-level page
  // (mirrors MemberSearch's `route` kind, NOT a MemberProfile tab). Persona-
  // gated ONLY (seeded to MEDICAL_ASSISTANT in roles.ts) — unlike the
  // flag-gated unpublished PriorityWorklist, Practice Schedule is a real
  // shipping surface with no VITE feature flag. Its route (/schedule) is a
  // keep-alive tab in TabbedAppShell and a classic <Route> in App.tsx, both
  // wrapped in PracticeScheduleGate (routeGates.tsx). NOTE the cross-package
  // parity test only compares `kind: "card"` entries, so this route entry
  // needs no matching admin-console change.
  PracticeSchedule: {
    kind: "route",
    label: "Practice Schedule",
    route: "/schedule",
  },
  // P4 host wiring (ADT Hospital/ED notification bell) — native @astrana/ui
  // module rendered as app-header CHROME (not a route, not a MemberProfile
  // tab), hence `kind: "feature"` rather than `route`/`tab`. Gated by persona
  // ALONE (this catalog entry + the roles.ts PROVIDER seed ARE the on/off
  // switch — no VITE build-time flag; a persona_modules grant needs no
  // redeploy) AND the Priority Worklist's own visibility, since the bell
  // hands every notification off to /worklist — see
  // components/useAdtBellVisible.ts.
  AdtNotifications: { kind: "feature", label: "ADT Notifications" },
  // Home card rendering ids (ACT-1303) — gate which of the Home-tab cards
  // render/order for the active persona. Distinct from the "Home" tab id
  // above (which gates the whole tab). Backend module_ids, byte-for-byte.
  //
  // HomeDemographics (ACT-1303-style, no ticket-number-specific behavior)
  // makes the Demographics card admin-configurable like the other 6 cards —
  // it flows through the SAME useVisibleHomeCards persona filter instead of
  // being spliced in as an always-on special case (see MemberProfilePage.tsx).
  // Its default EXPANDED state is carried in the per-module `actions[]` array
  // via a "collapsedByDefault" flag (no backend schema change) — see
  // MemberProfilePage.tsx's homeDemographicsActions/defaultExpanded wiring.
  // CM-8310 / CM-8319 — the member's completed next-best-action history,
  // sourced from the NBA engine's `nba_completion` store. A TAB (not a card):
  // it is an unbounded chronological feed, unlike the single recommendation the
  // HomeNextBestAction card shows. No `actions` — the feed is strictly
  // read-only, because the createAgentRun/updateAgentRun/deleteAgentRun
  // mutations are inert stubs server-side and the engine owns every write via
  // assignNba.
  //
  // ⚠️ Both NBA entries below are INERT BY DEFAULT. The feature is gated off by
  // `featureFlags.nextBestAction` (VITE_FEATURE_NEXT_BEST_ACTION, default
  // false) at its mount points in MemberProfilePage.tsx, so with the flag off
  // neither surface is ever mounted and these entries gate nothing. Keeping
  // them here (and in HOME_CARD_TYPES / CARD_MODULE_TYPES) means the admin
  // console's module list and the sort order are already correct when the flag
  // is flipped on. Do NOT treat a persona grant as the off-switch — that gate
  // fails open; see the flag's doc comment.
  Activity: { kind: "tab", label: "Activity", tabName: "Activity" },
  HomeNextBestAction: {
    kind: "card",
    // Label MUST match the admin console's FEATURE_CATALOG byte-for-byte. That
    // console now lives in apollomed/astraconnect-admin-console, so this parity
    // is hand-maintained — the cross-package test went away with packages/admin-console (#252).
    label: "Next Best Action",
    moduleType: CARD_MODULE_TYPES.HomeNextBestAction,
  },
  HomeDemographics: {
    kind: "card",
    label: "Demographics",
    moduleType: CARD_MODULE_TYPES.HomeDemographics,
  },
  HomeCareTeam: {
    kind: "card",
    // Label MUST match astraconnect-admin-console's FEATURE_CATALOG entry
    // byte-for-byte — that parity is hand-maintained (the cross-package test
    // went away with packages/admin-console, #252).
    label: "Care Team",
    moduleType: CARD_MODULE_TYPES.HomeCareTeam,
  },
  HomeClinicalSummary: {
    kind: "card",
    label: "Clinical Summary",
    moduleType: CARD_MODULE_TYPES.HomeClinicalSummary,
  },
  HomeInpatientEd: {
    kind: "card",
    // Label MUST match the admin-console's FEATURE_CATALOG byte-for-byte. The
    // admin console now lives in its own repo (apollomed/astraconnect-admin-console);
    // the former local cross-package equality test was removed with that package,
    // so this parity is no longer enforced here — keep the labels in sync by hand.
    label: "Inpatient & ED",
    moduleType: CARD_MODULE_TYPES.HomeInpatientEd,
  },
  HomeAnnualWellness: {
    kind: "card",
    label: "Annual Wellness",
    moduleType: CARD_MODULE_TYPES.HomeAnnualWellness,
  },
  // ACT-1342 (2026-07-29) — `actions` is DELIBERATELY left unregistered here.
  // A persona opts OUT of the interactive checklist (reverting to the old
  // read-only card) by having `"qualityGapsReadOnly"` in ITS OWN
  // `member_360.persona_modules.actions` row — see
  // MemberProfilePage.tsx's `qualityGapsReadOnlyForPersona`. Registering
  // `"qualityGapsReadOnly"` HERE instead would hand it to every consumer of
  // `catalogActions("HomeQualityGaps")` — the admin-ROLE bypass and the
  // no-persona/enforcement-off transition seam (see
  // useFeatureAccess.ts's `resolveModuleActions`, cases 0 and 1) — which
  // is the exact opposite of the intended default (checklist ON everywhere
  // until a persona explicitly opts out).
  HomeQualityGaps: {
    kind: "card",
    label: "Quality Gaps",
    moduleType: CARD_MODULE_TYPES.HomeQualityGaps,
  },
  HomeHcc: {
    kind: "card",
    // Label MUST match the admin-console's FEATURE_CATALOG byte-for-byte. The
    // admin console now lives in its own repo (apollomed/astraconnect-admin-console);
    // the former local cross-package equality test was removed with that package,
    // so this parity is no longer enforced here — keep the labels in sync by hand.
    label: "HCC Recapture",
    moduleType: CARD_MODULE_TYPES.HomeHcc,
  },
  HomeCases: {
    kind: "card",
    // T4 — renamed from "Cases" per the QA sheet. The SAME string has to land in
    // three places or the admin console and the app disagree about what this
    // card is called:
    //   1. here,
    //   2. astraconnect-admin-console's FEATURE_CATALOG (byte-for-byte — see
    //      the note on HomeHcc above; the cross-package equality test is gone),
    //   3. @astrana/ui's Cases module `Section title`.
    // The module id stays `HomeCases`; only the human label changes, so no
    // persona grant, backend module row or URL is affected.
    label: "Care Management",
    moduleType: CARD_MODULE_TYPES.HomeCases,
  },
};

// ---------------------------------------------------------------------------
// Guards / lookups
// ---------------------------------------------------------------------------
/**
 * Runtime guard for any externally-supplied module id (backend payloads, URL
 * params, etc.). The `roles.ts` seed types its values as plain `string[]`, and
 * `connectCurrentUser.currentPersona.modules[].moduleId` comes from a separate
 * service — neither carries a compile-time `ModuleId` guarantee. Validate before
 * trusting them. An unknown/mis-cased id (e.g. "documents") is rejected rather
 * than blindly cast to a value that never matches the catalog (which would hide
 * every gated surface for that persona). Module ids are case-sensitive.
 */
export function isModuleId(value: string): value is ModuleId {
  return Object.prototype.hasOwnProperty.call(FEATURE_CATALOG, value);
}

/**
 * Reverse index `tabName → ModuleId`, built ONCE at module load from the catalog
 * (instead of an O(n) scan on every `moduleIdForTabName` call). The catalog is a
 * static constant, so this map never needs rebuilding.
 */
const TAB_NAME_TO_MODULE_ID: ReadonlyMap<string, ModuleId> = new Map(
  (Object.keys(FEATURE_CATALOG) as ModuleId[]).flatMap((id) => {
    const entry = FEATURE_CATALOG[id];
    return entry.kind === "tab" && entry.tabName !== undefined
      ? [[entry.tabName, id] as const]
      : [];
  }),
);

/**
 * Reverse lookup: given a MemberProfile tab's `tabName`, return the ModuleId
 * that controls it, or `undefined` if no module gates that tab. O(1) via the
 * prebuilt reverse index.
 *
 * As of AC-ADM-3 (2026-08-21) EVERY tab MemberProfilePage renders has a catalog
 * entry, so `undefined` is now unreachable for a real tab: Activity (CM-8319),
 * Notes (G1), Documents, and finally Diagnoses / Labs / Scribe all resolve. The
 * `undefined` return survives only as the type-level contract for an
 * unrecognised tabName — do NOT reintroduce an "always shown" tab by omitting
 * its catalog entry; that bypasses both the persona gate and the admin's
 * per-persona tab ORDER, which is what AC-ADM-3 existed to fix.
 */
export function moduleIdForTabName(tabName: string): ModuleId | undefined {
  return TAB_NAME_TO_MODULE_ID.get(tabName);
}

/**
 * Returns the registered action keys for `moduleId` from the catalog — the
 * vocabulary of what the backend MAY enable for that module. Returns an empty
 * tuple when the module has no registered actions (no actions are expected for
 * that module). The access layer uses this as the allow-all fallback when no
 * raw persona payload is present (offline / dev override / backend absent).
 */
export function catalogActions(moduleId: ModuleId): readonly string[] {
  return FEATURE_CATALOG[moduleId].actions ?? [];
}

// ---------------------------------------------------------------------------
// Home card lookups (ACT-1303)
// ---------------------------------------------------------------------------
/**
 * Reverse index `ModuleType → card ModuleId` (the inverse of CARD_MODULE_TYPES),
 * built ONCE at module load. Backs `cardModuleIdForType`.
 */
const TYPE_TO_CARD_MODULE_ID: ReadonlyMap<ModuleType, ModuleId> = new Map(
  (Object.keys(CARD_MODULE_TYPES) as (keyof typeof CARD_MODULE_TYPES)[]).map(
    (id) => [CARD_MODULE_TYPES[id], id] as const,
  ),
);

/**
 * Is `id` one of the 9 Home card rendering ids (as opposed to a gating
 * ModuleId, or an unrelated backend `module_id` string)? Used to filter the
 * backend `raw.currentPersona.modules[]` payload down to Home-card entries
 * before building the sortOrder map in `useVisibleHomeCards`.
 */
export function isHomeCardModuleId(
  id: string,
): id is keyof typeof CARD_MODULE_TYPES {
  return Object.prototype.hasOwnProperty.call(CARD_MODULE_TYPES, id);
}

/**
 * Reverse lookup: given an @astrana/ui `ModuleType` (as it appears in a Home
 * tab card entry, e.g. `{ type: ModuleType.HCC }`), return the card ModuleId
 * the backend uses to reference it (e.g. `"HomeHcc"`), or `undefined` if
 * `type` is not one of the 9 Home card types. O(1) via the prebuilt reverse
 * index.
 */
export function cardModuleIdForType(type: ModuleType): string | undefined {
  return TYPE_TO_CARD_MODULE_ID.get(type);
}

// ---------------------------------------------------------------------------
// Single-source-of-truth guard (dev-only runtime check)
// ---------------------------------------------------------------------------
/**
 * Keeps the module-id vocabulary anchored to `roles.ts`. The PRIMARY guard is
 * now compile-time: `roles.ts` PERSONA_MODULES is typed `Record<string,
 * ModuleId[]>`, so any seed id outside the `ModuleId` union fails `tsc`. This
 * DEV-only runtime check is a redundant safety net (and catches the inverse — an
 * id present in the seed but somehow missing a FEATURE_CATALOG entry): at module
 * load in dev it warns if any seed id lacks a catalog entry. No-op in production.
 * The same drift is asserted as a HARD test in roles.test.ts (ACT-1319), where
 * PERSONA_MODULES is pinned as the non-runtime parity fixture.
 */
if (import.meta.env.DEV) {
  const missing = Object.values(PERSONA_MODULES)
    .flat()
    .filter((id) => !isModuleId(id));
  if (missing.length) {
    console.warn(
      `[featureCatalog] roles.ts PERSONA_MODULES references module id(s) with no FEATURE_CATALOG entry: ${[
        ...new Set(missing),
      ].join(", ")}. Add a catalog entry so persona/module identity stays in sync.`,
    );
  }
}
